<template>
  <div class="notifications-page">
    <div class="page-container">
      <!-- Toolbar (normal mode) -->
      <PageToolbar v-if="!selectionMode">
        <ToolbarHeading title="Notifications" :count="total" />
        <div class="header-tabs">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'active' }"
            @click="setTab('active')"
          >
            <font-awesome-icon icon="bell" />
            Active
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'archived' }"
            @click="setTab('archived')"
          >
            <font-awesome-icon icon="archive" />
            Archived
          </button>
        </div>

        <div class="toolbar-search-box">
          <font-awesome-icon icon="magnifying-glass" class="search-icon" />
          <input
            v-model="searchInput"
            type="text"
            class="search-input"
            placeholder="Search notifications..."
            @input="handleSearchInput"
          />
          <button v-if="searchInput" class="search-clear" @click="clearSearch">
            <font-awesome-icon icon="xmark" />
          </button>
        </div>

        <div class="toolbar-spacer"></div>

        <button class="btn-header" @click="enterSelectionMode()">
          <font-awesome-icon icon="check" />
          Select
        </button>
      </PageToolbar>

      <!-- Toolbar (selection mode) -->
      <PageToolbar v-else>
        <button class="btn-header" @click="toggleSelectAll">
          <font-awesome-icon icon="check" />
          Select All
        </button>
        <span class="selection-count">
          <span v-if="selectedCount === 0">{{ notifications.length }} on this page</span>
          <span v-else>{{ selectedCount }} of {{ notifications.length }} selected</span>
        </span>

        <div class="toolbar-spacer"></div>

        <button
          class="btn-header"
          :disabled="bulkProcessing"
          @click="bulkMarkAsRead"
        >
          <font-awesome-icon v-if="bulkProcessing" icon="spinner" spin />
          <font-awesome-icon v-else icon="envelope-open" />
          Mark as Read
        </button>
        <button
          class="btn-header"
          :disabled="bulkProcessing"
          @click="bulkArchive"
        >
          <font-awesome-icon v-if="bulkProcessing" icon="spinner" spin />
          <font-awesome-icon v-else icon="archive" />
          Archive
        </button>
        <button
          class="btn-header"
          :disabled="bulkProcessing"
          @click="exitSelectionMode"
        >
          Cancel
        </button>
      </PageToolbar>

      <!-- Loading State (initial load) -->
      <div v-if="loading && notifications.length === 0" class="loading-container">
        <font-awesome-icon icon="spinner" class="spinner" spin />
        <p class="loading-text">{{ searchInput ? 'Searching...' : 'Loading notifications...' }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <font-awesome-icon icon="triangle-exclamation" class="error-icon" />
        <p class="error-title">Failed to load notifications</p>
        <p class="error-message">{{ error }}</p>
        <button class="retry-button" @click="resetAndSearch">
          Try Again
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="notifications.length === 0 && !loading" class="empty-state">
        <font-awesome-icon :icon="activeTab === 'active' ? 'bell' : 'archive'" class="empty-icon" />
        <p class="empty-title">
          {{ searchInput ? 'No notifications found' : (activeTab === 'active' ? 'You\'re all caught up' : 'No archived notifications') }}
        </p>
        <p class="empty-description">
          {{ searchInput ? 'Try a different search term' : (activeTab === 'active' ? 'All your notifications will appear here' : 'Archived notifications will appear here') }}
        </p>
      </div>

      <!-- Notifications List -->
      <div v-else class="notifications-list">
        <div class="notification-grid">
          <ContentBlock
            v-for="notification in notifications"
            :key="notification.id"
            interactive
            :class="{
              'is-unread': !notification.is_read && activeTab === 'active',
              'is-selected': selectedIds.has(notification.id),
            }"
            @click="handleNotificationItemClick(notification)"
          >
            <div class="notification-item">
              <div class="notification-icon">
                <font-awesome-icon
                  :icon="getNotificationIcon(notification.severity)"
                  :class="getNotificationIconClass(notification.severity)"
                />
              </div>
              <div class="notification-content">
                <div class="notification-header">
                  <span class="notification-title">{{ notification.title }}</span>
                  <span class="notification-time">{{ moment(notification.created_at).fromNow() }}</span>
                </div>
                <p v-if="notification.email_message" class="notification-message">{{ notification.email_message.replace(/\n/g, ' ') }}</p>
              </div>
              <div v-if="!notification.is_read && activeTab === 'active' && !selectionMode" class="unread-indicator"></div>

              <!-- Quick Action Buttons (only when not in selection mode) -->
              <div v-if="!selectionMode" class="notification-actions">
                <button
                  v-if="!notification.is_read"
                  @click.stop="quickMarkAsRead(notification)"
                  class="btn-quick-action"
                  title="Mark as read"
                >
                  <font-awesome-icon icon="envelope-open" />
                </button>
                <button
                  v-if="!notification.is_archived"
                  @click.stop="quickArchive(notification)"
                  class="btn-quick-action"
                  title="Archive"
                >
                  <font-awesome-icon icon="archive" />
                </button>
              </div>
            </div>
          </ContentBlock>
        </div>

        <!-- Intersection Observer Trigger -->
        <div ref="loadMoreTrigger" class="load-more-trigger"></div>

        <!-- Loading More Indicator -->
        <div v-if="loadingMore" class="loading-more">
          <font-awesome-icon icon="spinner" class="spinner-small" spin />
          <span>Loading more...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useMainStore } from '@/stores/main'
import moment from 'moment'
import User from '@/api/User'
import { useToast } from 'vue-toastification'
import NotificationDetail from '@/components/SlideOver/NotificationDetail.vue'
import PageToolbar from '@/components/common/PageToolbar.vue'
import ToolbarHeading from '@/components/common/ToolbarHeading.vue'
import ContentBlock from '@/components/common/ContentBlock.vue'

const mainStore = useMainStore()
const toast = useToast()

// Tab state
const activeTab = ref('active')
const archivedOnly = computed(() => activeTab.value === 'archived')

// State
const notifications = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const error = ref('')
const searchInput = ref('')
const offset = ref(0)
const limit = 50
const total = ref(0)
const hasMore = ref(false)
const loadMoreTrigger = ref(null)
let observer = null
let searchTimeout = null

// Selection mode state
const selectionMode = ref(false)
const selectedIds = ref(new Set())
const bulkProcessing = ref(false)

// Computed properties for selection
const allCurrentPageSelected = computed(() => {
  if (notifications.value.length === 0) return false
  return notifications.value.every(n => selectedIds.value.has(n.id))
})
const selectedCount = computed(() => selectedIds.value.size)

// Fetch notifications from API
const fetchNotifications = async (isLoadMore = false) => {
  if (!hasMore.value && isLoadMore) return
  if (loading.value || loadingMore.value) return

  if (isLoadMore) {
    loadingMore.value = true
  } else {
    loading.value = true
  }

  error.value = ''

  try {
    const response = await User.getNotifications(offset.value, limit, searchInput.value, archivedOnly.value)

    if (response.status === 'success') {
      const responseData = response.data || {}
      const notificationsArray = responseData.notifications || []

      if (isLoadMore) {
        notifications.value = [...notifications.value, ...notificationsArray]
      } else {
        notifications.value = notificationsArray
      }

      total.value = responseData.total || 0
      hasMore.value = responseData.has_more || false
      offset.value += notificationsArray.length
    } else {
      error.value = response.error || 'Failed to load notifications'
      hasMore.value = false
    }
  } catch (err) {
    console.error('Error fetching notifications:', err)
    error.value = err.response?.data?.message || 'An error occurred while loading notifications'
    hasMore.value = false
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// Handle search input with debounce (300ms)
const handleSearchInput = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  searchTimeout = setTimeout(() => {
    resetAndSearch()
  }, 300)
}

// Reset offset and search
const resetAndSearch = () => {
  offset.value = 0
  notifications.value = []
  hasMore.value = true
  fetchNotifications()
}

// Clear the search box (used by the toolbar's x button)
const clearSearch = () => {
  searchInput.value = ''
  resetAndSearch()
}

// Set tab and reload
const setTab = (tab) => {
  if (activeTab.value !== tab) {
    activeTab.value = tab
    exitSelectionMode() // Clear selection when switching tabs
    resetAndSearch()
  }
}

// Quick mark as read (individual notification)
async function quickMarkAsRead(notification) {
  try {
    const response = await User.markNotificationRead(notification.id)
    if (response.status === 'success') {
      notification.is_read = true
      toast.success('Marked as read')

      // Update unread count
      const countResponse = await User.getUnreadNotificationCount()
      if (countResponse.status === 'success') {
        mainStore.update_notification_count(countResponse.count)
      }
    } else {
      toast.error('Failed to mark as read')
    }
  } catch (error) {
    console.error('Error marking notification as read:', error)
    toast.error('Failed to mark as read')
  }
}

// Quick archive (individual notification)
async function quickArchive(notification) {
  try {
    const response = await User.archiveNotification(notification.id)
    if (response.status === 'success') {
      toast.success('Archived')
      // Refresh the list
      resetAndSearch()
    } else {
      toast.error('Failed to archive')
    }
  } catch (error) {
    console.error('Error archiving notification:', error)
    toast.error('Failed to archive')
  }
}

// Enter selection mode
function enterSelectionMode() {
  selectionMode.value = true
  selectedIds.value.clear()
}

// Exit selection mode
function exitSelectionMode() {
  selectionMode.value = false
  selectedIds.value.clear()
}

// Toggle individual notification selection
function toggleNotificationSelection(notificationId) {
  if (selectedIds.value.has(notificationId)) {
    selectedIds.value.delete(notificationId)
  } else {
    selectedIds.value.add(notificationId)
  }
}

// Toggle select all on current page
function toggleSelectAll() {
  if (allCurrentPageSelected.value) {
    // Deselect all current page items
    notifications.value.forEach(n => selectedIds.value.delete(n.id))
  } else {
    // Select all current page items
    notifications.value.forEach(n => selectedIds.value.add(n.id))
  }
}

// Bulk mark as read
async function bulkMarkAsRead() {
  if (selectedIds.value.size === 0) return

  bulkProcessing.value = true
  const idsToProcess = Array.from(selectedIds.value)
  let successCount = 0
  let failCount = 0

  try {
    // Process each notification
    for (const id of idsToProcess) {
      try {
        const response = await User.markNotificationRead(id)
        if (response.status === 'success') {
          successCount++
          // Update local state
          const notification = notifications.value.find(n => n.id === id)
          if (notification) {
            notification.is_read = true
          }
        } else {
          failCount++
        }
      } catch (error) {
        console.error(`Error marking notification ${id} as read:`, error)
        failCount++
      }
    }

    // Update unread count
    const countResponse = await User.getUnreadNotificationCount()
    if (countResponse.status === 'success') {
      mainStore.update_notification_count(countResponse.count)
    }

    // Show result toast
    if (failCount === 0) {
      toast.success(`${successCount} notification${successCount !== 1 ? 's' : ''} marked as read`)
    } else if (successCount === 0) {
      toast.error(`Failed to mark ${failCount} notification${failCount !== 1 ? 's' : ''} as read`)
    } else {
      toast.warning(`${successCount} marked as read, ${failCount} failed`)
    }

    // Exit selection mode
    exitSelectionMode()

  } catch (error) {
    console.error('Bulk mark as read error:', error)
    toast.error('An unexpected error occurred')
  } finally {
    bulkProcessing.value = false
  }
}

// Bulk archive
async function bulkArchive() {
  if (selectedIds.value.size === 0) return

  bulkProcessing.value = true
  const idsToProcess = Array.from(selectedIds.value)
  let successCount = 0
  let failCount = 0

  try {
    // Process each notification
    for (const id of idsToProcess) {
      try {
        const response = await User.archiveNotification(id)
        if (response.status === 'success') {
          successCount++
        } else {
          failCount++
        }
      } catch (error) {
        console.error(`Error archiving notification ${id}:`, error)
        failCount++
      }
    }

    // Show result toast
    if (failCount === 0) {
      toast.success(`${successCount} notification${successCount !== 1 ? 's' : ''} archived`)
    } else if (successCount === 0) {
      toast.error(`Failed to archive ${failCount} notification${failCount !== 1 ? 's' : ''}`)
    } else {
      toast.warning(`${successCount} archived, ${failCount} failed`)
    }

    // Refresh the list to remove archived items (if on active tab)
    resetAndSearch()

    // Exit selection mode
    exitSelectionMode()

  } catch (error) {
    console.error('Bulk archive error:', error)
    toast.error('An unexpected error occurred')
  } finally {
    bulkProcessing.value = false
  }
}

// Setup intersection observer for infinite scroll
const setupIntersectionObserver = () => {
  if (!loadMoreTrigger.value) return

  observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries
      if (entry.isIntersecting && hasMore.value && !loadingMore.value) {
        fetchNotifications(true)
      }
    },
    {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    }
  )

  observer.observe(loadMoreTrigger.value)
}

// Handle notification item click (checks selection mode)
function handleNotificationItemClick(notification) {
  if (selectionMode.value) {
    // In selection mode: toggle checkbox
    toggleNotificationSelection(notification.id)
  } else {
    // Normal mode: open slideover
    handleNotificationClick(notification)
  }
}

// Handle notification click (opens slideover and marks as read)
async function handleNotificationClick(notification) {
  // Open slideover immediately
  mainStore.show_slideover(
    NotificationDetail,
    { notification: notification },
    { width: 50 },
    notification.title
  )

  // Mark as read if not already read
  if (!notification.is_read) {
    try {
      const response = await User.markNotificationRead(notification.id)
      if (response.status === 'success') {
        // Update local state
        notification.is_read = true

        // Update unread count
        const countResponse = await User.getUnreadNotificationCount()
        if (countResponse.status === 'success') {
          mainStore.update_notification_count(countResponse.count)
        }
      }
      // Silent failure - don't show error toast
    } catch (error) {
      console.error('Error marking notification as read:', error)
      // Silent failure - don't show error toast
    }
  }
}

// Get notification icon based on severity
function getNotificationIcon(severity) {
  switch (severity) {
    case 0:
      return 'circle-info'
    case 1:
    case 2:
      return 'triangle-exclamation'
    case 3:
    case 4:
      return 'circle-exclamation'
    default:
      return 'bell'
  }
}

// Get notification icon class based on severity
function getNotificationIconClass(severity) {
  switch (severity) {
    case 0:
      return 'icon-info'
    case 1:
      return 'icon-low'
    case 2:
      return 'icon-medium'
    case 3:
      return 'icon-high'
    case 4:
      return 'icon-critical'
    default:
      return 'icon-default'
  }
}

// Lifecycle hooks
onMounted(() => {
  fetchNotifications()

  // Setup observer after initial load
  setTimeout(() => {
    setupIntersectionObserver()
  }, 100)

  // Listen for notification updates
  mainStore.event_on('notification-updated', () => {
    resetAndSearch()
  })
})

onBeforeUnmount(() => {
  if (observer && loadMoreTrigger.value) {
    observer.unobserve(loadMoreTrigger.value)
    observer.disconnect()
  }
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
})
</script>

<style scoped>
/* Full-bleed page: content flows naturally and #main-scroll-container scrolls
   (it supplies `padding: 1rem 1rem 1rem 0`, so we keep left padding at 0 to
   align flush with the sidebar). */
.notifications-page {
  background: var(--surface-page);
}

.page-container {
  width: 100%;
  padding: 0 0 2rem 0;
}

/* Gap between the toolbar row and the notifications list below. .page-header is
   the root rendered by <PageToolbar/>; pierce its scope to add margin. */
:deep(.page-header) {
  margin-bottom: 1rem;
}

/* Loading State */
.loading-container {
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.spinner {
  font-size: 2.5rem;
  color: var(--accent-text);
  margin-bottom: 1rem;
}

.loading-text {
  color: var(--text-muted);
  font-size: 0.875rem;
  margin: 0;
}

/* Error State */
.error-container {
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.error-icon {
  font-size: 3rem;
  color: var(--color-error);
  margin-bottom: 1rem;
  opacity: 0.8;
}

.error-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-error);
  margin: 0 0 0.5rem 0;
}

.error-message {
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0 0 1.5rem 0;
  text-align: center;
}

.retry-button {
  padding: 0.625rem 1.5rem;
  background: rgba(168, 85, 247, 0.15);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: var(--radius-md);
  color: var(--accent-text);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
}

.retry-button:hover {
  background: rgba(168, 85, 247, 0.25);
  border-color: rgba(168, 85, 247, 0.5);
}

/* Empty State */
.empty-state {
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 3rem;
  color: var(--text-faint);
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-muted);
  margin: 0 0 0.5rem 0;
}

.empty-description {
  font-size: 0.875rem;
  color: var(--text-faint);
  margin: 0;
  text-align: center;
}

/* Notifications List */
.notifications-list {
  display: flex;
  flex-direction: column;
}

.notification-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

/* Outer shell is <ContentBlock interactive>; .is-unread and .is-selected are
   the row-level state modifiers applied on the ContentBlock root. */
:deep(.content-block.is-unread) {
  background: rgba(168, 85, 247, 0.06);
  border-left: 3px solid var(--accent-text);
}

:deep(.content-block.is-selected) {
  background: var(--surface-overlay-3);
}

/* Inner card layout (icon + content + actions). The outer ContentBlock
   already supplies the surface, padding, and hover. */
.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  position: relative;
}

.notification-icon {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  background: var(--surface-overlay-2);
  font-size: 1.25rem;
}

.icon-info {
  color: var(--color-info);
}

.icon-low {
  color: var(--color-success);
}

.icon-medium {
  color: var(--color-warning);
}

.icon-high {
  color: var(--color-feed);
}

.icon-critical {
  color: var(--color-error);
}

.icon-default {
  color: var(--accent-text);
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.notification-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
}

.notification-time {
  font-size: 0.75rem;
  color: var(--text-faint);
  white-space: nowrap;
  flex-shrink: 0;
}

.notification-message {
  font-size: 0.875rem;
  color: var(--text-muted);
  line-height: 1.5;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unread-indicator {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 8px;
  height: 8px;
  background: var(--accent-text);
  border-radius: var(--radius-pill);
}

/* Load More Trigger */
.load-more-trigger {
  height: 20px;
  margin: 20px 0;
}

/* Loading More */
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.5rem;
  color: var(--text-muted);
  font-size: 0.875rem;
}

.spinner-small {
  font-size: 1rem;
  color: var(--accent-text);
}

/* Selection count text — sits between the Select-All button and the bulk
   actions in the selection-mode toolbar. */
.selection-count {
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 500;
}

/* Quick Action Buttons */
.notification-actions {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.notification-item:hover .notification-actions {
  opacity: 1;
  pointer-events: auto;
}

.btn-quick-action {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--surface-page);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-quick-action:hover {
  background-color: var(--surface-raised);
  border-color: rgba(255, 255, 255, 0.3);
  color: var(--text-primary);
}

/* Responsive Design */
@media (max-width: 768px) {
  .notification-icon {
    width: 2rem;
    height: 2rem;
    font-size: 1rem;
  }

  .notification-header {
    flex-direction: column;
    gap: 0.25rem;
  }

  .notification-time {
    align-self: flex-start;
  }
}
</style>
